import classnames from "classnames";
import { useDrag } from "react-dnd";

import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import { useTranslation } from "@/lib/i18n";
import timeSeriesBlockStyles from "@/features/widget-forms/forms/weather-model-map/components/time-series-config-card.module.css";

import type { TimeSeriesParameterEntry } from "./display-group-board-sync";
import {
  DISPLAY_GROUP_POOL_PARAMETER_TYPE,
  type DisplayGroupPoolDragItem,
} from "./display-group-drag-types";
import styles from "./display-group-parameter-pool.module.css";

type DisplayGroupParameterChipProps = {
  entry: TimeSeriesParameterEntry;
  parameters: TimeSeriesParameterOption[];
  isUsed: boolean;
};

function DisplayGroupParameterChip({
  entry,
  parameters,
  isUsed,
}: DisplayGroupParameterChipProps) {
  const [{ isDragging }, drag] = useDrag({
    type: DISPLAY_GROUP_POOL_PARAMETER_TYPE,
    item: (): DisplayGroupPoolDragItem => ({ parameterId: entry.parameterId }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const catalog = parameters.find((p) => p.id === entry.parameterId);
  const catalogLabel = catalog?.label ?? entry.parameterId;

  return (
    <div
      ref={drag}
      className={classnames(styles.chip, timeSeriesBlockStyles.parameterBlock, {
        [styles.chipDragging]: isDragging,
        [styles.chipUsed]: isUsed,
      })}
    >
      <div className={classnames(timeSeriesBlockStyles.parameterLabel, styles.chipTitle)}>
        {entry.label}
      </div>
      <div className={classnames(timeSeriesBlockStyles.parameterMeta, styles.chipMeta)}>
        <span className={timeSeriesBlockStyles.catalogLabel}>{catalogLabel}</span>
      </div>
    </div>
  );
}

type DisplayGroupParameterPoolProps = {
  entries: TimeSeriesParameterEntry[];
  parameters: TimeSeriesParameterOption[];
  usedParameterIds: Set<string>;
};

export function DisplayGroupParameterPool({
  entries,
  parameters,
  usedParameterIds,
}: DisplayGroupParameterPoolProps) {
  const { t } = useTranslation();

  if (entries.length === 0) {
    return (
      <div className={styles.pool}>
        <p className={styles.poolHint}>
          {t("admin.widgets.fields.observations_map.marker_designs.pool_empty_time_series")}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.pool}>
      <h4 className={styles.poolTitle}>
        {t("admin.widgets.fields.observations_map.marker_designs.pool_title")}
      </h4>
      <p className={styles.poolHint}>
        {t("admin.widgets.fields.observations_map.marker_designs.pool_hint")}
      </p>
      <div className={styles.chips}>
        {entries.map((entry) => (
          <DisplayGroupParameterChip
            key={entry.parameterId}
            entry={entry}
            parameters={parameters}
            isUsed={usedParameterIds.has(entry.parameterId)}
          />
        ))}
      </div>
    </div>
  );
}
